import { config } from '../../config';
import type { BoardTileView } from '../../app/lockedBoard';
import { usePointerGesture } from '../../ui/capture/usePointerGesture';
import type { LockedBoardGesturesB } from './useLockedBoardB';

/**
 * B-10 — one tile of the locked board's 2×4 grid (design spec §2.4; scenario 12): hypothesis
 * text, status-colored border, count ring. The whole tile is the increment target (≥60 pt via
 * the inline `MIN_TOUCH_TARGET_PT` floor); swipe-left cycles the status override.
 *
 * The data attributes are the proven contract, unchanged in meaning from B-9's rows:
 * `data-hid` (target resolved at `pointerdown` — no reflow can retarget a begun tap),
 * `data-ack` (toggled imperatively in the commit moment, never React state),
 * `data-pressed` (receipt of touch within one frame — CSS only), `data-status` (§2.3's
 * never-hue-only coding: confirmed = filled heavier ring + heavier border, disproved = strike
 * + red, invalidated = reduced opacity — all restyles, never geometry).
 *
 * `touch-action: none` (stylesheet): swipe-left lives on this surface and the grid never
 * scrolls, so there is no pan for the browser to claim — the tile takes the whole gesture.
 * That differs from a Discovery row (`manipulation`) by requirement, not preference.
 *
 * Long-press opens B-15's hypothesis detail sheet (annotate, voice note, override buttons).
 * The hold fires from the gesture layer's own timer while the finger is down, so a
 * ≥`LONG_PRESS_MS` press classifies as a hold and never increments. The undo affordance is
 * **B-11's** bottom-band pill — B tiles carry no `−1` inset (that is A's board, not this one).
 */
export interface BoardTileBProps {
  readonly tile: BoardTileView;
  readonly gestures: LockedBoardGesturesB;
  /** `false` before kickoff: the tile renders, contacts are declined at `onPressStart`. */
  readonly capturing: boolean;
}

export function BoardTileB({ tile, gestures, capturing }: BoardTileBProps) {
  const handlers = usePointerGesture({
    onPressStart: gestures.onPressStart,
    onTap: gestures.onTap,
    onLongPress: gestures.onLongPress,
    onSwipeLeft: gestures.onSwipeLeft,
    onPressEnd: gestures.onPressEnd,
    onActiveChange: gestures.onActiveChange,
    // Scenario 12/LIVE-5 — swipe-left-to-override exists only on the locked board in B.
    swipeLeft: true,
  });

  return (
    <div
      className="boardb-tile"
      data-hid={tile.id}
      data-role="tile"
      data-status={tile.status}
      data-capturing={capturing ? 'true' : 'false'}
      data-testid={`boardb-tile-${tile.id}`}
      style={{ minHeight: `${String(config.MIN_TOUCH_TARGET_PT)}px` }}
      {...handlers}
    >
      <span className="boardb-tile__text">{tile.text}</span>
      {/* The dominant numeral (skill §2/§11): rendered, never ranked — `nextBoardOrder`'s
          parameter type cannot see a count, which is scenario 12's stability as a compile
          fact rather than a promise. The ring is the second channel of the status signal. */}
      <span className="boardb-count" data-testid={`boardb-count-${tile.id}`}>
        {tile.cachedCount}
      </span>
    </div>
  );
}

/**
 * LOCK-7 — an unfilled grid slot: an explicit empty state that cannot be tapped as an
 * increment. **There are no gesture handlers on this element and no `data-hid`** — that is the
 * mechanism, not a disabled flag: `resolveTarget` walks up for `[data-hid]`, finds none, and
 * returns before anything is classified, so there is nothing to build a command from.
 * Explicit rather than blank because a blank cell on a glanceable 2×4 grid reads as a
 * rendering fault; a short board is legal and the grid says so.
 */
export function EmptyBoardSlotB({ index }: { readonly index: number }) {
  return (
    <div
      className="boardb-tile boardb-tile--empty"
      data-testid={`boardb-empty-${String(index)}`}
      style={{ minHeight: `${String(config.MIN_TOUCH_TARGET_PT)}px` }}
    >
      <span className="boardb-tile__empty-note">Empty slot</span>
    </div>
  );
}
